export type StrategyBlock =
  | { kind: 'p'; text: string }
  | { kind: 'accent'; text: string }
  | { kind: 'sub'; text: string }
  | { kind: 'list'; items: string[] }

export interface StrategySection {
  number: string
  title: string
  blocks: StrategyBlock[]
}

export const strategyMeta = {
  title: 'Campaign Strategy — Precision Phenotyping in DME',
  subtitle: 'Scientific-first engagement strategy for Denmark',
  note: 'Internal document. Not part of the HCP-facing journey and not counted as an HCP interaction.',
  coreIdea: 'Earn the treatment conversation through precision phenotyping.',
  coreSupport:
    'By beginning with independent scientific value rather than treatment promotion, the campaign seeks to build credibility with a highly selective specialist audience and create the foundation for a later discussion about inflammatory DME and more individualised clinical decision-making.',
  finalLine:
    'Before we talk about how DME is treated, we first need to look more precisely at what type of DME we are treating.',
}

export const strategySections: StrategySection[] = [
  {
    number: '01',
    title: 'Market context',
    blocks: [
      {
        kind: 'p',
        text: 'The Danish ophthalmology environment presents a particular engagement challenge. The specialist audience is small, highly scientific and selective in how it engages with pharmaceutical companies.',
      },
      {
        kind: 'p',
        text: 'Traditional promotional communication therefore risks being dismissed before the message has established sufficient clinical relevance.',
      },
      {
        kind: 'p',
        text: 'At the same time, treatment decisions in DME can involve meaningful patient burden. Where the ideal candidate is difficult to identify with confidence, clinicians may understandably be cautious about exposing patients to additional intervention without a sufficiently clear rationale.',
      },
      {
        kind: 'p',
        text: 'The communication challenge is therefore broader than product awareness. The first task is not to persuade ophthalmologists to consider a treatment. The first task is to create confidence that:',
      },
      { kind: 'accent', text: 'DME can be characterised more precisely.' },
    ],
  },
  {
    number: '02',
    title: 'Strategic opportunity',
    blocks: [
      {
        kind: 'p',
        text: 'Recent AI-assisted OCT research creates an opportunity to begin the conversation at a different point: diagnostic precision before treatment discussion.',
      },
      {
        kind: 'p',
        text: 'Rather than beginning with therapy, the campaign begins with OCT-based phenotyping. The Midena analysis demonstrates that familiar OCT variables can be analysed together to identify distinct morphological DME phenotypes.',
      },
      { kind: 'accent', text: 'From measuring fluid to recognising phenotype.' },
      {
        kind: 'p',
        text: 'The implication is not that a phenotype automatically determines treatment. Instead, the campaign introduces the idea that more precise treatment decisions begin with more precise characterisation of the patient.',
      },
    ],
  },
  {
    number: '03',
    title: 'Why the campaign is phased',
    blocks: [
      {
        kind: 'p',
        text: 'The campaign is deliberately divided into stages. The audience should first be given a reason to reconsider how DME is characterised before being asked to reconsider how it may be treated.',
      },
      { kind: 'sub', text: 'Phase 1 — Precision phenotyping' },
      {
        kind: 'p',
        text: 'Completely unbranded and purely scientific. Focus on OCT, AI-assisted analysis, DME heterogeneity, morphological phenotyping and precision diagnostics.',
      },
      {
        kind: 'p',
        text: 'Out of scope: Ozurdex, dexamethasone, implants, treatment switching, treatment superiority and specific treatment pathways.',
      },
      {
        kind: 'p',
        text: 'The objective is to establish that DME is not one morphological pattern. The desired response is: “Perhaps there is more information in the OCT than fluid volume alone.”',
      },
      { kind: 'sub', text: 'Phase 2 — Understanding inflammatory DME' },
      {
        kind: 'p',
        text: 'Once phenotype-based characterisation is established, the science can deepen: inflammatory characteristics within DME, OCT biomarker patterns, inflammatory and structural differences between patients, expert interpretation and recognition of appropriate patient types.',
      },
      {
        kind: 'p',
        text: 'The question progresses from “Can we phenotype DME more precisely?” to “What might these phenotypes tell us about underlying disease biology?”',
      },
      { kind: 'sub', text: 'Phase 3 — Clinical decision-making' },
      {
        kind: 'p',
        text: 'Only once the scientific foundation is established should communication move toward treatment: precision imaging → precision phenotyping → informed treatment decisions.',
      },
      {
        kind: 'p',
        text: 'Avoid implying that the Midena phenotype model itself dictates treatment.',
      },
    ],
  },
  {
    number: '04',
    title: 'Why this is an unusual teaser',
    blocks: [
      {
        kind: 'p',
        text: 'A conventional teaser is designed to generate awareness → curiosity → action. This teaser has a different strategic role.',
      },
      { kind: 'accent', text: 'Credibility → scientific curiosity → permission for the next conversation.' },
      {
        kind: 'p',
        text: 'The primary reward is scientific insight, not a product message. The teaser is closer to a short scientific thought-starter than a promotional communication. Its job is to:',
      },
      {
        kind: 'list',
        items: [
          'Earn attention',
          'Establish credibility',
          'Introduce a genuinely new scientific perspective',
          'Reduce resistance to future communication',
          'Create curiosity around inflammatory DME',
          'Prepare the audience for deeper educational content',
        ],
      },
      {
        kind: 'p',
        text: 'The absence of a product message is intentional. It is the strategy.',
      },
    ],
  },
  {
    number: '05',
    title: 'The 5-page story',
    blocks: [
      { kind: 'sub', text: 'Page 1 — Reframe the OCT' },
      {
        kind: 'p',
        text: '“What if OCT could reveal phenotype — not just fluid?” Introduces the possibility that OCT may provide a richer morphological picture of DME, with the Midena data establishing immediate credibility. Strategic role: challenge the frame without criticising current practice.',
      },
      { kind: 'sub', text: 'Page 2 — Establish heterogeneity' },
      {
        kind: 'p',
        text: '“DME is not one morphological pattern.” Connects established clinical heterogeneity with the possibility of systematic phenotyping. Takeaway: same diagnosis, different phenotype.',
      },
      { kind: 'sub', text: 'Page 3 — Explain the inputs' },
      {
        kind: 'p',
        text: '“5 OCT variables. One clustering model.” Shows the phenotypes are based on familiar clinical variables — IRF · SRF · I-HRF · ELM · EZ — without teaching basic OCT anatomy. Strategic role: make the AI output clinically credible rather than opaque.',
      },
      { kind: 'sub', text: 'Page 4 — Deliver the reveal' },
      {
        kind: 'p',
        text: '“One diagnosis. Four phenotypes.” Shows the four morphological clusters identified through the combined OCT biomarker profiles, turning heterogeneity into something visually recognisable. They are morphological phenotype clusters — not treatment groups, treatment-response categories or validated disease stages.',
      },
      { kind: 'sub', text: 'Page 5 — Deeper scientific engagement' },
      {
        kind: 'p',
        text: '“Let’s look at a few together.” Converts scientific curiosity into a low-friction next step — book a phenotyping walkthrough. The next action remains scientific rather than promotional: bring your own scans, explore OCT-based phenotyping, discuss biomarker patterns, engage at your own pace.',
      },
    ],
  },
  {
    number: '06',
    title: 'The hidden quiz layer',
    blocks: [
      {
        kind: 'p',
        text: 'The hidden quiz and answer pages are an optional engagement layer, not the conceptual backbone of the Snak. Their role is to provide optional interaction, peer comparison and an additional engagement signal.',
      },
      {
        kind: 'p',
        text: 'Users who prefer a direct scientific journey are never forced through quiz mechanics.',
      },
    ],
  },
  {
    number: '07',
    title: 'Why this approach fits the audience',
    blocks: [
      {
        kind: 'accent',
        text: 'For a small, highly specialised audience, credibility is more valuable than promotional intensity.',
      },
      {
        kind: 'p',
        text: 'Instead of asking the audience to believe a treatment claim, the first interaction asks them to consider published OCT evidence. Instead of beginning with a product, it begins with a clinical question. Instead of telling ophthalmologists how to treat, it gives them a new way to think about how DME may be characterised.',
      },
      {
        kind: 'p',
        text: 'The campaign must first demonstrate: “This is worth my attention as a clinician.” Only later should it ask: “What could this mean for treatment?”',
      },
    ],
  },
]
